import React, { useState, useEffect } from 'react';
import api from '../api';
import { Users, TrendingUp } from 'lucide-react';

/**
 * Teacher Load widget — compact matrix Teacher × Vertical → active student count.
 * Slots inside Admin Dashboard for load-balancing at a glance.
 */
export default function TeacherLoadWidget() {
  const [data, setData] = useState({ verticals: [], rows: [], grand_total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/teacher-load');
        setData(res.data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const cellColor = (n) => {
    if (n === 0) return 'text-slate-300';
    if (n <= 2) return 'text-emerald-700 bg-emerald-50';
    if (n <= 5) return 'text-amber-700 bg-amber-50';
    return 'text-red-700 bg-red-50 font-bold';
  };

  return (
    <div className="nb-card p-5" data-testid="teacher-load-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#0F172A] text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-600" /> Teacher Load View
          <span className="text-[10px] text-slate-400 font-normal">Active students per vertical</span>
        </h3>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold" data-testid="teacher-load-total">
          <TrendingUp className="w-3 h-3 inline mr-1" /> {data.grand_total} total
        </span>
      </div>
      {loading ? (
        <p className="text-sm text-slate-400 py-3">Loading…</p>
      ) : data.rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-3">No teacher assignments yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-2 px-2 font-bold text-slate-600 sticky left-0 bg-white z-10">Teacher</th>
                {data.verticals.map(v => (
                  <th key={v} className="text-center py-2 px-2 font-bold text-slate-600 whitespace-nowrap">{v}</th>
                ))}
                <th className="text-center py-2 px-2 font-bold text-indigo-700">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.teacher_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`load-row-${r.teacher_id}`}>
                  <td className="py-2 px-2 font-semibold text-slate-900 sticky left-0 bg-white">{r.teacher_name}</td>
                  {data.verticals.map(v => (
                    <td key={v} className={`text-center py-2 px-2 font-semibold ${cellColor(r.counts[v] || 0)}`} data-testid={`load-cell-${r.teacher_id}-${v}`}>
                      {r.counts[v] || '·'}
                    </td>
                  ))}
                  <td className="text-center py-2 px-2 font-bold text-indigo-700">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
